import {useMemo, useState} from 'react'
import {useQuery} from '@apollo/client'
import {Alert, Badge, Button, Card, Spinner, Table} from 'react-bootstrap'
import {FormattedMessage, MessageDescriptor, defineMessages, useIntl} from 'react-intl'
import {parseId} from '../../../helpers'
import {
  GET_ACA_VERIFICATIONS_BY_POLICY,
  IResACAVerifications,
  ACAVerificationRecord,
} from '../../../gql/queries/acaVerificationsQuery'
import {GET_ONE_POLICY_ATTACHMENTS_API} from '../../../gql/queries/policiesApiQueries'
import {IEdgesAttachments} from '../../../types/policiesApi'
import {ModalACAVerification} from './ModalACAVerification'

type ResultColor = 'green' | 'yellow' | 'red' | 'blue'

const emDash = '—'
const dotSep = ' · '

/** Los 4 resultados reusan las llaves ya existentes del filtro ACA de la lista (§17.10). */
const RESULT_MSG: Record<ResultColor, MessageDescriptor> = defineMessages({
  green: {id: 'policies.list.filters.aca.green', defaultMessage: 'Paid & Completed'},
  yellow: {id: 'policies.list.filters.aca.yellow', defaultMessage: 'Paid / Pending Documents'},
  red: {id: 'policies.list.filters.aca.red', defaultMessage: 'Unpaid / Pending'},
  blue: {id: 'policies.list.filters.aca.blue', defaultMessage: '$0 Premium / Completed'},
}) as Record<ResultColor, MessageDescriptor>

const RESULT_STYLE: Record<ResultColor, {bg: string; textClass: string}> = {
  green: {bg: '#198754', textClass: 'text-white'},
  yellow: {bg: '#ffc107', textClass: 'text-dark'},
  red: {bg: '#dc3545', textClass: 'text-white'},
  blue: {bg: '#0d6efd', textClass: 'text-white'},
}

const ResultBadge = ({result}: {result: string}) => {
  const intl = useIntl()
  const color = result as ResultColor
  const style = RESULT_STYLE[color]
  if (!style) return <span className='text-muted'>{emDash}</span>
  return (
    <span className={`badge ${style.textClass}`} style={{backgroundColor: style.bg}}>
      {intl.formatMessage(RESULT_MSG[color])}
    </span>
  )
}

const formatDate = (value?: string | null) => {
  if (!value) return emDash
  try {
    return new Date(value).toLocaleString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  } catch {
    return value
  }
}

const userLabel = (u?: {firstName?: string; lastName?: string} | null) => {
  if (!u) return emDash
  return `${u.firstName || ''} ${u.lastName || ''}`.trim() || emDash
}

type Props = {
  policyId: string
  documentsDueDate?: string | null
  hasOpenTickets: boolean
  canAdd: boolean
  canChange: boolean
  members?: import('../../../types/policiesApi').EdgeMemberPolicy[]
}

export const ACAVerificationsTab = ({
  policyId,
  documentsDueDate,
  hasOpenTickets,
  canAdd,
  canChange,
  members = [],
}: Props) => {
  const normalizedId = useMemo(() => Number(parseId(policyId)), [policyId])

  const {data, loading, error, refetch} = useQuery<IResACAVerifications>(
    GET_ACA_VERIFICATIONS_BY_POLICY,
    {
      variables: {policyId: normalizedId},
      skip: !normalizedId,
      fetchPolicy: 'no-cache',
    }
  )

  const {data: docsData} = useQuery<{
    policiesById: {policyAttachmentsSet: {edges: IEdgesAttachments[]}}
  }>(GET_ONE_POLICY_ATTACHMENTS_API, {
    variables: {id: parseId(policyId)},
    skip: !policyId,
    fetchPolicy: 'no-cache',
  })

  const allPolicyDocs = docsData?.policiesById?.policyAttachmentsSet?.edges || []

  const [modalOpen, setModalOpen] = useState(false)
  const [editTarget, setEditTarget] = useState<ACAVerificationRecord | undefined>()
  const [verificationDocs, setVerificationDocs] = useState<IEdgesAttachments[]>([])

  const openNew = () => {
    setEditTarget(undefined)
    setVerificationDocs([])
    setModalOpen(true)
  }

  const openEdit = (v: ACAVerificationRecord) => {
    const verificationDay = v.created.slice(0, 10)
    const docsForDay = allPolicyDocs.filter(
      ({node}) => node.created?.slice(0, 10) === verificationDay
    )
    setVerificationDocs(docsForDay)
    setEditTarget(v)
    setModalOpen(true)
  }

  const handleClose = () => {
    setModalOpen(false)
    setEditTarget(undefined)
    setVerificationDocs([])
  }

  const handleSaved = () => {
    refetch()
  }

  const records = data?.acaVerificationsByPolicy ?? []

  return (
    <div className='cardContainer'>
      <Card>
        <Card.Header className='d-flex align-items-center justify-content-between'>
          <span className='fw-bold'>
            <i className='bi bi-clipboard2-check me-2 text-primary' /> <FormattedMessage
              id='policies.detail.aca.title'
              defaultMessage='ACA Verifications'
            />
          </span>
          {canAdd && (
            <Button size='sm' variant='primary' onClick={openNew}>
              <i className='bi bi-plus-lg me-1' /> <FormattedMessage
                id='policies.detail.aca.newVerification'
                defaultMessage='New Verification'
              />
            </Button>
          )}
        </Card.Header>

        <Card.Body className='p-0'>
          {loading && (
            <div className='text-center py-4'>
              <Spinner animation='border' size='sm' />
              <span className='ms-2 text-muted'>
                <FormattedMessage
                  id='policies.detail.aca.loading'
                  defaultMessage='Loading verifications…'
                />
              </span>
            </div>
          )}

          {error && (
            <Alert variant='danger' className='m-3'>
              <FormattedMessage
                id='policies.detail.aca.errorLoading'
                defaultMessage='Error loading verifications: {message}'
                values={{message: error.message}}
              />
            </Alert>
          )}

          {!loading && !error && records.length === 0 && (
            <div className='text-center text-muted py-4 fst-italic'>
              <FormattedMessage
                id='policies.detail.aca.empty'
                defaultMessage='No ACA verifications recorded yet.'
              />
            </div>
          )}

          {!loading && records.length > 0 && (
            <Table hover responsive className='mb-0' size='sm'>
              <thead className='table-light'>
                <tr>
                  <th>
                    <FormattedMessage id='policies.detail.aca.th.date' defaultMessage='Date' />
                  </th>
                  <th>
                    <FormattedMessage id='common.field.user' defaultMessage='User' />
                  </th>
                  <th>
                    <FormattedMessage id='policies.detail.aca.th.result' defaultMessage='Result' />
                  </th>
                  <th>
                    <FormattedMessage id='policies.detail.aca.th.payment' defaultMessage='Payment' />
                  </th>
                  <th>
                    <FormattedMessage id='policies.detail.aca.th.docs' defaultMessage='Documents' />
                  </th>
                  <th>
                    <FormattedMessage
                      id='policies.detail.aca.th.lastUpdatedBy'
                      defaultMessage='Last updated by'
                    />
                  </th>
                  {canChange && <th />}
                </tr>
              </thead>
              <tbody>
                {records.map((v) => (
                  <tr key={v.id}>
                    <td className='text-nowrap'>{formatDate(v.created)}</td>
                    <td>{userLabel(v.createdBy)}</td>
                    <td>
                      <ResultBadge result={v.result} />
                    </td>
                    <td>
                      {v.paymentStatus === 'up_to_date' && (
                        <Badge bg='success'>
                          <FormattedMessage id='policies.detail.aca.payment.upToDate' defaultMessage='Up to date' />
                        </Badge>
                      )}
                      {v.paymentStatus === 'pending' && (
                        <Badge bg='warning' text='dark'>
                          <FormattedMessage id='policies.detail.aca.payment.pending' defaultMessage='Pending' />
                        </Badge>
                      )}
                      {v.paymentStatus === 'zero_payment' && (
                        <Badge bg='secondary'>
                          <FormattedMessage id='policies.detail.aca.payment.zero' defaultMessage='$0 Payment' />
                        </Badge>
                      )}
                      {!v.paymentStatus && emDash}
                    </td>
                    <td>
                      {v.pendingDocuments === 'yes' && (
                        <Badge bg='danger'>
                          <FormattedMessage id='policies.detail.aca.docs.pending' defaultMessage='Pending' />
                        </Badge>
                      )}
                      {v.pendingDocuments === 'no' && (
                        <Badge bg='success'>
                          <FormattedMessage id='policies.detail.aca.docs.none' defaultMessage='None' />
                        </Badge>
                      )}
                      {v.pendingDocuments === 'na' && (
                        <Badge bg='secondary'>
                          <FormattedMessage id='common.value.notAvailable' defaultMessage='N/A' />
                        </Badge>
                      )}
                      {!v.pendingDocuments && emDash}
                    </td>
                    <td className='text-muted small'>
                      {v.updatedBy && v.updated
                        ? userLabel(v.updatedBy) + dotSep + formatDate(v.updated)
                        : emDash}
                    </td>
                    {canChange && (
                      <td>
                        <Button
                          size='sm'
                          variant='outline-secondary'
                          onClick={() => openEdit(v)}
                        >
                          <i className='bi bi-pencil' />
                        </Button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {modalOpen && (
        <ModalACAVerification
          isOpen={modalOpen}
          onClose={handleClose}
          policyId={policyId}
          documentsDueDate={documentsDueDate}
          hasOpenTickets={hasOpenTickets}
          verificationToEdit={editTarget}
          onSaved={handleSaved}
          canChange={canChange}
          members={members}
          verificationDocs={verificationDocs}
        />
      )}
    </div>
  )
}
